/**
 * NieuweStopWaarschuwing — "er is een stop bijgekomen nadat de route vast stond"
 *
 * Aanleiding (WB-322910, 01-09-2026): route GPR-038197- werd om 13:55 vergrendeld,
 * de aflever-stop kwam er om 22:34 bij en de inpasser gaf hem nummer 28 —
 * achteraan, en visueel identiek aan de 27 stops ervoor. De bus was toen al
 * ingepakt, dus het kleed lag er niet in. De chauffeur reed de dag uit zonder
 * te weten dat er iets nieuws bij zat.
 *
 * De klasse: een stop met `added_after_lock` is aangeplakt nádat de route (en
 * dus meestal de bus) klaar was. De planner toont daar al een NIEUW-badge op;
 * de field-app deed niets. Dit blok sluit dat gat aan de chauffeurskant.
 *
 * Waarom niet gewaarschuwd op "kleed niet ingeladen": gemeten over 60 dagen
 * hadden 91 aflever-stops een item zonder `ingeladen`-vinkje, en 90 daarvan
 * zijn gewoon afgerond. Het vinkje wordt simpelweg lang niet altijd gezet, dus
 * dat signaal is 99% vals alarm en zou binnen een week weggeklikt worden.
 * `added_after_lock` is wel schoon: 84 stops in 60 dagen, waarvan 25 aflever.
 *
 * Alleen een AFLEVER-stop vraagt om bevestiging, want alleen daar hoort lading
 * in de bus. Een na-lock ophaal-stop krijgt de badge en de melding, maar geen
 * knop: een bevestiging zonder inhoud maakt wegklikken een gewoonte.
 */

import { useState } from 'react';
import { decodeHtmlEntities } from '../../lib/utils/decode-html';
import clsx from 'clsx';
import { WbButton, WbChip, WbFlag } from '../wb';
import type { GeplandeRitWithDetails } from '../../lib/salesforce';

interface NieuweStopWaarschuwingProps {
  /** Nog te rijden stops die na de vergrendeling zijn toegevoegd. */
  stops: GeplandeRitWithDetails[];
  /** Stop-ids waarvoor de chauffeur de lading al bevestigde. */
  bevestigd: Set<string>;
  onBevestig: (stopId: string) => void;
  /** Chauffeur heeft het kleed niet — meldt het bij kantoor. */
  onMeldOntbrekend: (stop: GeplandeRitWithDetails) => Promise<void>;
}

/** Adres uit SF bevat entity-encoded <br>; één regel voor in het blok. */
function adresOpEenRegel(adres: string): string {
  return decodeHtmlEntities(adres).replace(/<[^>]*>/g, ', ').replace(/\s+/g, ' ').replace(/ ,/g, ',').trim();
}

/**
 * "gisteravond 22:34" / "vanochtend 07:10" / "zaterdag 16:20" — de chauffeur
 * moet kunnen inschatten of dit vóór of ná het inpakken van zijn bus was.
 */
function toegevoegdOp(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;

  const klok = new Intl.DateTimeFormat('nl-NL', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Amsterdam',
  }).format(d);

  // Dagverschil in Amsterdamse kalenderdagen, niet in etmalen van 24 uur.
  const dagSleutel = (x: Date) =>
    new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Amsterdam' }).format(x);
  const vandaag = dagSleutel(new Date());
  const gisteren = dagSleutel(new Date(Date.now() - 24 * 60 * 60 * 1000));
  const stopDag = dagSleutel(d);

  const uur = Number(klok.slice(0, 2));
  if (stopDag === vandaag) return `vanochtend om ${klok}`;
  if (stopDag === gisteren) return uur >= 18 ? `gisteravond om ${klok}` : `gisteren om ${klok}`;

  const dag = new Intl.DateTimeFormat('nl-NL', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: 'Europe/Amsterdam',
  }).format(d);
  return `${dag} om ${klok}`;
}

export function NieuweStopWaarschuwing({
  stops,
  bevestigd,
  onMeldOntbrekend,
  onBevestig,
}: NieuweStopWaarschuwingProps) {
  const [bezig, setBezig] = useState<string | null>(null);
  const [gemeld, setGemeld] = useState<Set<string>>(new Set());
  const [fout, setFout] = useState<string | null>(null);

  // Openstaand = aflever-stop waarvan de lading nog niet is bevestigd of gemeld.
  const openstaand = stops.filter(
    (s) => s.Type__c === 'Terugbrengen' && !bevestigd.has(s.Id) && !gemeld.has(s.Id)
  );

  // Ingeklapt tenzij er iets te beantwoorden is. Vier bijgekomen ophaal-stops
  // zijn een regel, geen scherm: de chauffeur moet bij de eerste stop kunnen
  // zonder eerst voorbij de melding te scrollen (feedback Willem 02-09).
  const [open, setOpen] = useState(false);
  const uitgeklapt = open || openstaand.length > 0;

  if (stops.length === 0) return null;

  const nummers = stops.map((s) => s.Route_Sequence__c ?? '?').join(', ');

  const handleMeld = async (stop: GeplandeRitWithDetails) => {
    setBezig(stop.Id);
    setFout(null);
    try {
      await onMeldOntbrekend(stop);
      setGemeld((prev) => new Set(prev).add(stop.Id));
    } catch {
      setFout('Melden mislukt. Bel kantoor even.');
    } finally {
      setBezig(null);
    }
  };

  return (
    <div className="space-y-2">
      {/* Kop: mint = "nieuw" (zelfde semantiek als de NIEUW-strook op de stopkaart), tikbaar */}
      <WbFlag
        kind="nieuw"
        title={stops.length === 1 ? `Stop ${nummers} is later bijgekomen` : `${stops.length} stops later bijgekomen: ${nummers}`}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="flex items-center justify-between gap-2">
          <span className="text-sm">{uitgeklapt ? 'Toegevoegd nadat de route al vaststond. Mogelijk was de bus toen al ingepakt.' : 'Tik voor details'}</span>
          <svg className={clsx('w-5 h-5 shrink-0 transition-transform', uitgeklapt && 'rotate-180')} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </span>
      </WbFlag>

      {uitgeklapt && stops.map((stop) => {
        const isAflever = stop.Type__c === 'Terugbrengen';
        const isBevestigd = bevestigd.has(stop.Id);
        const isGemeld = gemeld.has(stop.Id);
        const moment = toegevoegdOp(stop.addedAt);

        return (
          <div key={stop.Id} className="bg-wb-vlak border-wb border-wb-mint rounded-wb px-3 py-2 space-y-2">
            <div className="flex items-center gap-2">
              <span className="w-9 h-9 shrink-0 rounded-wb bg-wb-mint text-wb-mintink font-wb-heading font-bold text-lg flex items-center justify-center">
                {stop.Route_Sequence__c ?? '?'}
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-wb-heading font-bold text-base text-wb-inkt truncate">{stop.customerName || stop.workOrderName || 'Stop'}</p>
                {stop.address && <p className="font-mono text-sm text-wb-inkt2 truncate">{adresOpEenRegel(stop.address)}</p>}
              </div>
              <WbChip kind={isAflever ? 'klaar' : 'wacht'}>{isAflever ? 'Afleveren' : 'Ophalen'}</WbChip>
            </div>

            {moment && <p className="text-sm text-wb-inkt2">Toegevoegd {moment}.</p>}

            {isAflever && !isBevestigd && !isGemeld && (
              <>
                <p className="font-semibold text-wb-inkt">Heb je dit kleed in de bus?</p>
                <div className="grid grid-cols-2 gap-2">
                  <WbButton disabled={bezig === stop.Id} onClick={() => onBevestig(stop.Id)}>Ja, ligt erin</WbButton>
                  <WbButton variant="secondary" disabled={bezig === stop.Id} isLoading={bezig === stop.Id} onClick={() => handleMeld(stop)}>Nee, meld het</WbButton>
                </div>
              </>
            )}

            {isAflever && isBevestigd && <p className="text-sm font-semibold text-wb-inkt">Bevestigd: kleed ligt in de bus.</p>}
            {isGemeld && <p className="text-sm font-semibold text-wb-inkt">Gemeld bij kantoor. Rijd deze stop niet; kantoor belt de klant.</p>}
          </div>
        );
      })}

      {fout && <WbFlag kind="geld">{fout}</WbFlag>}

      {openstaand.length > 0 && (
        <p className="text-sm text-wb-inkt2">Controleer dit vóór je wegrijdt. Ligt het kleed er niet in, dan haal je het nu nog op in de wasserij.</p>
      )}
    </div>
  );
}
